import { variablesToParameters } from 'features/sql/utils'
import { Provider, WorkflowVariable } from '@cloud-native/workflows-engine'
import { Workflow } from '../types'
import { SQL_API } from '../../sql/api/urls'
import settings from '../../../settings'

interface FormatQueryCallParams {
  name: string
  variables: WorkflowVariable[]
  provider: Provider
  hasOutputNode: boolean
}

export const formatQueryCall = ({ name, variables, provider, hasOutputNode }: FormatQueryCallParams) =>
  `CALL ${name}(${variablesToParameters({ variables, provider, hasOutputNode })})`

/**
 * Extracts the function name from the result of formatQueryCall
 * @param queryCall - The string resulting from formatQueryCall (e.g. "CALL my_function(@param1, @param2)")
 * @returns The extracted function name
 */
export const extractNameFromQueryCall = (queryCall: string): string => {
  // Searches for the pattern "CALL function_name(" and extracts the name
  const match = queryCall.match(/^CALL\s+([^(]+)\(/)
  if (!match) {
    return ''
  }
  // Remove backticks and trim whitespace
  return match[1].replace(/`/g, '').trim()
}

export const formatWorkflowUrl = (workflow: Workflow, isWorkflowSync: boolean) => {
  return `${SQL_API}/${workflow?.connectionName}/${isWorkflowSync ? 'query' : 'job'}`
}

export const formatWorkflowPayload = (workflow: Workflow, isWorkflowSync: boolean) => {
  const payload: Record<string, unknown> = {
    queryParameters: JSON.parse(workflow.config.mcpTool.procedure?.params ?? '{}')
  }
  if (isWorkflowSync) {
    payload.q = workflow.config.mcpTool.procedure?.call ?? ''
  } else {
    payload.query = workflow.config.mcpTool.procedure?.call ?? ''
  }
  return payload
}

export const formatGetCodeSnippet = (workflow: Workflow, isWorkflowSync: boolean) => {
  const accessToken = `access_token=${workflow.token}`

  switch (workflow.config.connectionProvider) {
    case Provider.DatabricksRest: {
      const url = `${settings.apiBaseUrl}/v3/workflows-jobs/${workflow.connectionName}`
      const jobName = JSON.parse(workflow?.config?.procedure?.call ?? '{}')?.name
      return `${url}/run/${jobName}?${accessToken}`
    }
    default: {
      const url = formatWorkflowUrl(workflow, isWorkflowSync)
      const q = isWorkflowSync ? 'q' : 'query'
      const query = encodeURIComponent(workflow.config?.procedure?.call || '')
      const queryParameters = encodeURIComponent(workflow.config?.procedure?.params || '{}')
      return `${url}/?${q}=${query}&queryParameters=${queryParameters}&${accessToken}`
    }
  }
}

export const formatPostCodeSnippet = (workflow: Workflow, isWorkflowSync: boolean) => {
  const contentType = `curl -H 'Content-Type: application/json'`
  const authorization = `-H 'Authorization: Bearer ${workflow.token}'`

  switch (workflow.config.connectionProvider) {
    case Provider.DatabricksRest: {
      const url = `${settings.apiBaseUrl}/v3/workflows-jobs/${workflow.connectionName}`
      const jobName = JSON.parse(workflow?.config?.procedure?.call ?? '{}')?.name
      return `${contentType} \\
     ${authorization} \\
     -X POST \\
     ${url}/run/${jobName}`
    }
    default: {
      const url = formatWorkflowUrl(workflow, isWorkflowSync)
      const q = isWorkflowSync ? 'q' : 'query'
      const query = workflow.config?.procedure?.call || ''
      const queryParameters = workflow.config?.procedure?.params || '{}'
      return `${contentType} \\
     ${authorization} \\
     -d '{"${q}": "${query}",
          "queryParameters": ${queryParameters}}' \\
     -X POST \\
     ${url}`
    }
  }
}
